import { defineStore } from 'pinia';
import { ref, computed, nextTick } from 'vue';
import { cloneDeep } from 'lodash';

import { useRouterStore } from '@apps/main/new-desktop/core/stores/routerStore';
import {
  OldTripPlannerView,
  TripPlannerPage,
} from '@apps/main/new-desktop/core/models/navigation';

import { isHotelIngredient, isFlightIngredient } from '../recipe/ingredients';
import type {
  HotelIngredient,
  FlightIngredient,
  FerryIngredient,
  RecipeMainIngredient,
} from '../recipe/ingredients';
import { isOwnStayIngredient } from '../recipe/ingredients/typeGuards';

export const useEditingIngredientStore = defineStore(
  'editingIngredientStore',
  () => {
    const routerStore = useRouterStore();

    // State
    const editingHotelIngredient = ref<HotelIngredient | null>(null);
    const editingFlightIngredient = ref<FlightIngredient | null>(null);
    const editingFlightIngredientStartingStatus = ref<FlightIngredient | null>(
      null
    );
    const editingFerryIngredient = ref<FerryIngredient | null>(null);
    const editingFerryIngredientStartingStatus = ref<FerryIngredient | null>(
      null
    );
    const flightIngredientFrom = ref<'recipe' | 'itinerary' | null>(null);
    const ferryIngredientFrom = ref<'recipe' | 'itinerary' | null>(null);

    // Getters
    const editingTransportIngredient = computed<
      FlightIngredient | FerryIngredient | null
    >(() =>
      editingFlightIngredient.value
        ? editingFlightIngredient.value
        : editingFerryIngredient.value
    );

    // Actions
    function setEditingIngredient(
      ingredient: RecipeMainIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      if (isOwnStayIngredient(ingredient)) {
        throw new Error('Unable to edit own stay ingredients');
      }

      if (isHotelIngredient(ingredient)) {
        setHotelEditingIngredient(ingredient);
      } else {
        setTransportEditingIngredient(ingredient, from);
      }
    }

    function setHotelEditingIngredient(ingredient: HotelIngredient): void {
      editingHotelIngredient.value = ingredient;
    }

    function setFlightEditingIngredient(
      ingredient: FlightIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      flightIngredientFrom.value = from;
      saveFlightIngredientStartingStatus(ingredient);
      editingFlightIngredient.value = ingredient;
    }

    function setFerryEditingIngredient(
      ingredient: FerryIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      ferryIngredientFrom.value = from;
      saveFerryIngredientStartingStatus(ingredient);
      editingFerryIngredient.value = ingredient;
    }

    function setTransportEditingIngredient(
      ingredient: FerryIngredient | FlightIngredient,
      from: 'recipe' | 'itinerary'
    ): void {
      isFlightIngredient(ingredient)
        ? setFlightEditingIngredient(ingredient, from)
        : setFerryEditingIngredient(ingredient, from);
    }

    function saveFlightIngredientStartingStatus(
      ingredient?: FlightIngredient
    ): void {
      editingFlightIngredientStartingStatus.value = ingredient
        ? cloneDeep(ingredient)
        : null;
    }

    function saveFerryIngredientStartingStatus(
      ingredient?: FerryIngredient
    ): void {
      editingFerryIngredientStartingStatus.value = ingredient
        ? cloneDeep(ingredient)
        : null;
    }

    async function doneEditingIngredient(): Promise<void> {
      if (editingHotelIngredient.value) {
        editingHotelIngredient.value.polygonConstraint = undefined;
      }
      editingHotelIngredient.value = null;
      editingFlightIngredient.value = null;
      editingFerryIngredient.value = null;
      editingFlightIngredientStartingStatus.value = null;
      editingFerryIngredientStartingStatus.value = null;

      if (
        routerStore.getPreviousTripPlannerView(
          OldTripPlannerView.ProductList
        ) === TripPlannerPage.TripOverview
      ) {
        await nextTick();
        routerStore.navigateToTripOverview();
      }
    }

    return {
      // State
      editingHotelIngredient,
      editingFlightIngredient,
      editingFlightIngredientStartingStatus,
      editingFerryIngredient,
      editingFerryIngredientStartingStatus,
      flightIngredientFrom,
      ferryIngredientFrom,

      // Getters
      editingTransportIngredient,

      // Actions
      setEditingIngredient,
      setHotelEditingIngredient,
      setFlightEditingIngredient,
      setFerryEditingIngredient,
      setTransportEditingIngredient,
      saveFlightIngredientStartingStatus,
      saveFerryIngredientStartingStatus,
      doneEditingIngredient,
    };
  }
);
